import { useState, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAlert } from "../../../contexts/AlertContext";
import {
  useMySystemRoles,
  useMyGroupMemberships,
  useOrganizations,
  useGroupTree,
  useFeideGroups,
  useToggleHiddenGroup,
  useToggleHiddenFeideGroup,
  useToggleActiveGroup,
  useCreateGroup,
} from "../../../api/hooks";
import {
  IconCirclePlus,
  IconMembers,
  IconEye,
  IconEyeOff,
  IconCircleChevronDown,
  IconCircleChevronUp,
  IconSync,
  IconFileImport,
} from "../../../common/Icons/Icons";
import CreateSubgroupModal from "./CreateSubgroupModal";
import ManageMembersModal from "./ManageMembersModal";
import ImportOrMapFeideGroupModal from "./ImportOrMapFeideGroupModal";
import styles from "./OrganizationSettings.module.css";

// Control visibility of hidden groups toggle
const SHOW_HIDDEN_TOGGLE = false;

const OrganizationSettings = () => {
  const { t } = useTranslation("common");
  const history = useHistory();
  const { prompt } = useAlert();

  // State
  const [showHidden, setShowHidden] = useState(true); // Default to showing hidden groups
  const [showInactive, setShowInactive] = useState(false); // Default to hiding inactive organizations
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // Modal state
  const [showCreateSubgroupModal, setShowCreateSubgroupModal] = useState(false);
  const [showManageMembersModal, setShowManageMembersModal] = useState(false);
  const [showImportOrMapModal, setShowImportOrMapModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  // React Query hooks
  const { data: systemRolesData, isLoading: rolesLoading } = useMySystemRoles();
  const {
    data: membershipsData,
    isLoading: membershipsLoading,
  } = useMyGroupMemberships(showInactive);
  const {
    data: organizationsData,
    isLoading: orgsLoading,
  } = useOrganizations(showInactive);

  // Mutations
  const toggleHiddenGroupMutation = useToggleHiddenGroup();
  const toggleHiddenFeideGroupMutation = useToggleHiddenFeideGroup();
  const toggleActiveGroupMutation = useToggleActiveGroup();
  const createGroupMutation = useCreateGroup();

  const systemRoles = systemRolesData?.data || [];
  const groupMemberships = membershipsData?.data || [];
  const isSystemAdmin = systemRoles.includes("systemadmin");

  const isLoading = rolesLoading || membershipsLoading || orgsLoading;

  // Calculate organizations to display
  // The backend API filters organizations based on active status and includeInactive parameter
  const organizations = useMemo(() => {
    return organizationsData?.data || [];
  }, [organizationsData]);

  // Permission helpers
  const canManageOrg = useCallback(
    (orgId) => {
      if (isSystemAdmin) return true;

      // Check if user has admin access to the organization or any of its groups
      const hasAdminAccess = groupMemberships.some(
        (m) => m.role?.name === "admin"
      );

      return hasAdminAccess;
    },
    [isSystemAdmin, groupMemberships]
  );

  // Check if user can manage a specific group
  // User can manage a group if they have admin role in that group OR any ancestor
  const canManageGroup = useCallback(
    (groupId, treeData) => {
      if (isSystemAdmin) return true;

      // Find the group in the tree
      const findGroupInTree = (groups, targetId) => {
        for (const group of groups) {
          if (group.id === targetId) return group;
          if (group.children) {
            const found = findGroupInTree(group.children, targetId);
            if (found) return found;
          }
        }
        return null;
      };

      const group = findGroupInTree(treeData || [], groupId);
      if (!group) return false;

      // Build list of group and all its ancestors
      const getAncestorIds = (grp, allGroups) => {
        const ancestors = [grp.id];
        let current = grp;

        while (current.parentGroupId) {
          ancestors.push(current.parentGroupId);
          current = findGroupInTree(allGroups, current.parentGroupId);
          if (!current) break;
        }

        return ancestors;
      };

      const ancestorIds = getAncestorIds(group, treeData || []);

      // Check if user has admin role in this group or any ancestor
      return groupMemberships.some(
        (m) => m.role?.name === "admin" && ancestorIds.includes(m.groupId)
      );
    },
    [isSystemAdmin, groupMemberships]
  );

  // Toggle expand/collapse
  const toggleNode = useCallback((nodeKey) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeKey)) {
        newSet.delete(nodeKey);
      } else {
        newSet.add(nodeKey);
      }
      return newSet;
    });
  }, []);

  // Action handlers
  const handleCreateSubgroup = (group, organizationId) => {
    setSelectedGroup(group);
    setSelectedOrganization(organizationId);
    setShowCreateSubgroupModal(true);
  };

  const handleManageMembers = (group, organizationId) => {
    setSelectedGroup(group);
    setSelectedOrganization(organizationId);
    setShowManageMembersModal(true);
  };

  const handleImportOrMap = (feideGroup, organizationId) => {
    setSelectedGroup(feideGroup);
    setSelectedOrganization(organizationId);
    setShowImportOrMapModal(true);
  };

  const handleToggleHidden = async (group, isFeide, organizationId) => {
    try {
      if (isFeide) {
        await toggleHiddenFeideGroupMutation.mutateAsync({
          feideGroupId: group.id,
          organizationId,
        });
      } else {
        await toggleHiddenGroupMutation.mutateAsync({
          groupId: group.id,
        });
      }
    } catch (err) {
      console.error("Toggle hidden failed:", err);
    }
  };

  const handleToggleActive = async (groupId, currentActive) => {
    try {
      await toggleActiveGroupMutation.mutateAsync({
        id: groupId,
        active: !currentActive,
      });
    } catch (err) {
      console.error("Toggle active failed:", err);
    }
  };

  const handleCreateOrganization = async () => {
    const orgName = await prompt(
      t("auth.settings.organizations.createNewOrganization"),
      t("auth.settings.organizations.organizationName")
    );
    if (!orgName) return;

    try {
      await createGroupMutation.mutateAsync({
        name: orgName,
        type: "organization",
      });
    } catch (err) {
      console.error("Create organization failed:", err);
    }
  };

  // const handleModalSuccess = () => {
  //   refetchOrganizations();
  // };

  if (isLoading) {
    return (
      <section className={styles.section}>
        <div className={styles.loading}>
          {t("auth.settings.organizations.loading")}
        </div>
      </section>
    );
  }

  return (
    <section className={styles.section}>
      {(SHOW_HIDDEN_TOGGLE || isSystemAdmin) && (
        <div className={styles.header}>
          {/*<h2 className={styles.sectionTitle}>{t("auth.settings.organizations.title")}</h2>*/}
          <div className={styles.headerActions}>
            {SHOW_HIDDEN_TOGGLE && (
              <label className={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={showHidden}
                  onChange={(e) => setShowHidden(e.target.checked)}
                />
                {t("auth.settings.feideGroups.showHidden")}
              </label>
            )}
            {isSystemAdmin && (
              <>
                <label className={styles.toggleLabel}>
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                  />
                  {t("auth.settings.organizations.showInactive")}
                </label>
                <button onClick={handleCreateOrganization} className={styles.createOrgButton}>
                  {t("auth.settings.organizations.createOrganization")}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {organizations.length === 0 ? (
        <div className={styles.emptyState}>
          <p>{t("auth.settings.organizations.noOrganizations")}</p>
          <p className={styles.hint}>{t("auth.settings.organizations.noOrganizationsHint")}</p>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          {/* Table Header */}
          <div className={styles.tableHeader}>
            <div className={styles.tableHeaderCell}>Gruppenavn</div>
            <div className={styles.tableHeaderCell}>Gruppa finst i</div>
            <div className={styles.tableHeaderCell}>Handlingar</div>
            <div className={styles.tableHeaderCell}></div>
          </div>

          {/* Table Body */}
          <div className={styles.tableBody}>
            {organizations.map((org) => (
              <OrganizationRow
                key={org.id}
                organization={org}
                level={0}
                expandedNodes={expandedNodes}
                onToggleNode={toggleNode}
                canManage={canManageOrg(org.id)}
                canManageGroup={canManageGroup}
                groupMemberships={groupMemberships}
                showHidden={showHidden}
                isSystemAdmin={isSystemAdmin}
                showInactive={showInactive}
                onCreateSubgroup={handleCreateSubgroup}
                onManageMembers={handleManageMembers}
                onImportOrMap={handleImportOrMap}
                onToggleHidden={handleToggleHidden}
                onToggleActive={handleToggleActive}
              />
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      <CreateSubgroupModal
        isOpen={showCreateSubgroupModal}
        onClose={() => setShowCreateSubgroupModal(false)}
        parentGroup={selectedGroup}
        organizationId={selectedOrganization}
        // onSuccess={handleModalSuccess}
      />

      <ManageMembersModal
        isOpen={showManageMembersModal}
        onClose={() => setShowManageMembersModal(false)}
        group={selectedGroup}
        organizationId={selectedOrganization}
        canManage={canManageOrg(selectedOrganization)}
      />

      <ImportOrMapFeideGroupModal
        isOpen={showImportOrMapModal}
        onClose={() => setShowImportOrMapModal(false)}
        feideGroup={selectedGroup}
        organizationId={selectedOrganization}
        // onSuccess={handleModalSuccess}
      />
    </section>
  );
};

// Recursive row component for organizations
const OrganizationRow = ({
  organization,
  level,
  expandedNodes,
  onToggleNode,
  canManage,
  canManageGroup,
  groupMemberships,
  showHidden,
  isSystemAdmin,
  showInactive,
  onCreateSubgroup,
  onManageMembers,
  onImportOrMap,
  onToggleHidden,
  onToggleActive,
}) => {
  const { data: treeData } = useGroupTree(organization.id, showHidden);
  const { data: feideData } = useFeideGroups(organization.id, showHidden);

  const tree = treeData?.data || [];
  const feideGroups = feideData?.data || [];

  // Build integrated tree with FEIDE groups
  const integratedTree = useMemo(() => {
    if (!tree.length && !feideGroups.length) return [];

    // Create a map of Digup groups
    const groupMap = new Map();
    tree.forEach((node) => {
      groupMap.set(node.id, {
        ...node,
        children: [],
        type: "digup",
        isFeide: false,
      });
    });

    // Build Digup group hierarchy
    tree.forEach((node) => {
      if (node.parentGroupId && node.parentGroupId !== organization.id) {
        const parent = groupMap.get(node.parentGroupId);
        if (parent) {
          parent.children.push(groupMap.get(node.id));
        }
      }
    });

    // Build FEIDE group hierarchy
    const feideNodeMap = new Map();
    feideGroups.forEach((feideGroup) => {
      feideNodeMap.set(feideGroup.id, {
        ...feideGroup,
        children: [],
        isFeide: true,
        name: feideGroup.name,
        type: "feide",
      });
    });

    // Build FEIDE parent-child relationships
    feideGroups.forEach((feideGroup) => {
      if (feideGroup.parentFeideGroupId) {
        const parent = feideNodeMap.get(feideGroup.parentFeideGroupId);
        const child = feideNodeMap.get(feideGroup.id);
        if (parent && child) {
          parent.children.push(child);
        }
      }
    });

    // Track which FEIDE nodes have been added
    const addedFeideNodes = new Set();

    // Integrate FEIDE groups into Digup tree
    feideGroups.forEach((feideGroup) => {
      const feideNode = feideNodeMap.get(feideGroup.id);

      // If FEIDE group is mapped to a Digup group, attach metadata
      if (feideGroup.mappings && feideGroup.mappings.length > 0) {
        const digupGroupId = feideGroup.mappings[0].digupGroupId;
        const digupGroup = groupMap.get(digupGroupId);
        if (digupGroup) {
          digupGroup.feideGroup = feideGroup;
          addedFeideNodes.add(feideGroup.id);
        }
      }
    });

    // Add unmapped FEIDE nodes to the tree
    feideGroups.forEach((feideGroup) => {
      if (addedFeideNodes.has(feideGroup.id)) return;

      const feideNode = feideNodeMap.get(feideGroup.id);

      if (feideGroup.parentFeideGroupId) {
        const parentFeide = feideGroups.find(
          (g) => g.id === feideGroup.parentFeideGroupId
        );
        if (
          parentFeide &&
          parentFeide.mappings &&
          parentFeide.mappings.length > 0
        ) {
          const parentDigupGroupId = parentFeide.mappings[0].digupGroupId;

          // If parent is mapped to the organization itself, add as root (not as child of org)
          if (parentDigupGroupId === organization.id) {
            // Don't add to organization node, it will be added as root below
            addedFeideNodes.add(feideGroup.id);
          } else {
            // Parent is mapped to a different DigUp group (subgroup), add as child
            const parentDigupGroup = groupMap.get(parentDigupGroupId);
            if (parentDigupGroup) {
              parentDigupGroup.children.push(feideNode);
              addedFeideNodes.add(feideGroup.id);
            }
          }
        }
      }
    });

    // Return root nodes (direct children of organization)
    const roots = Array.from(groupMap.values())
      .filter(
        (node) =>
          node.id !== organization.id && // Exclude the organization itself
          (!node.parentGroupId || node.parentGroupId === organization.id)
      )
      .concat(
        // Add unmapped root FEIDE groups (only if they don't have their own mappings)
        Array.from(feideNodeMap.values()).filter(
          (node) => {
            if (node.id === organization.id) return false; // Never include org itself

            // Skip if this Feide group has its own mapping (it's shown as DigUp group already)
            const originalFeideGroup = feideGroups.find(g => g.id === node.id);
            if (originalFeideGroup?.mappings && originalFeideGroup.mappings.length > 0) {
              return false; // Has mapping, will be shown as DigUp group
            }

            if (addedFeideNodes.has(node.id) && node.parentFeideGroupId) {
              // This was marked as added but has a parent - check if parent maps to org
              const parentFeide = feideGroups.find(g => g.id === node.parentFeideGroupId);
              if (parentFeide?.mappings?.[0]?.digupGroupId === organization.id) {
                return true; // Add as root since parent maps to org (unmapped children only)
              }
            }
            // Otherwise only add if no parent and not added elsewhere
            return !node.parentFeideGroupId && !addedFeideNodes.has(node.id);
          }
        )
      );

    // Helper to serialize tree recursively
    const serializeNode = (node) => ({
      id: node.id,
      name: node.name,
      type: node.isFeide ? "feide" : "digup",
      hidden: node.hidden,
      parentGroupId: node.parentGroupId,
      parentFeideGroupId: node.parentFeideGroupId,
      hasFeideMapping: !node.isFeide && !!node.feideGroup,
      feideGroupId: node.feideGroup?.id,
      children: node.children?.map(serializeNode) || [],
    });

    const debugData = {
      organization: {
        id: organization.id,
        name: organization.name,
      },
      rawData: {
        digupGroupsCount: tree.length,
        feideGroupsCount: feideGroups.length,
        digupGroups: tree.map((g) => ({
          id: g.id,
          name: g.name,
          parentGroupId: g.parentGroupId,
        })),
        feideGroups: feideGroups.map((g) => ({
          id: g.id,
          name: g.name,
          parentFeideGroupId: g.parentFeideGroupId,
          mappings: g.mappings,
        })),
      },
      integratedTree: {
        rootCount: roots.length,
        roots: roots.map(serializeNode),
      },
    };

    console.log(
      `[TABLE DATA] ${organization.name} (ID: ${organization.id})`,
      JSON.stringify(debugData, null, 2)
    );

    return roots;
  }, [tree, feideGroups, organization.id]);

  const nodeKey = `org-${organization.id}`;
  const isExpanded = expandedNodes.has(nodeKey);
  const hasChildren = integratedTree.length > 0;

  // Check if organization has FEIDE mapping
  const hasFeideMapping = feideGroups.some((fg) =>
    fg.mappings?.some((m) => m.digupGroupId === organization.id)
  );

  // Check if user can manage this organization
  const canManageThisOrg = canManageGroup ? canManageGroup(organization.id, tree) : false;

  const paddingLeft = level * 2; // 2rem per level

  return (
    <>
      {/* Organization Row */}
      <div className={`${styles.tableRow} ${styles.topRow}`}>
        {/* Column 1: Name with expand button */}
        <div
          className={styles.tableCell}
          style={{ paddingLeft: `${paddingLeft}rem` }}
        >
          <div className={styles.nameCell}>
            {hasChildren && (
              <button
                className={styles.expandButton}
                onClick={() => onToggleNode(nodeKey)}
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? (
                  <IconCircleChevronUp />
                ) : (
                  <IconCircleChevronDown />
                )}
              </button>
            )}
            <span className={`${styles.groupName} ${!organization.active ? styles.inactiveOrg : ''}`}>
              {organization.name}
              <span className={styles.groupId}> (ID: {organization.id})</span>
            </span>
          </div>
        </div>

        {/* Column 2: Source */}
        <div className={styles.tableCell}>
          {hasFeideMapping ? (
            <div className={styles.syncedSource}>
              <span className={styles.sourceText}>Feide</span>
              <IconSync className={styles.syncIcon} />
              <span className={styles.sourceText}>DigUp</span>
            </div>
          ) : (
            <span className={styles.sourceText}>DigUp</span>
          )}
        </div>

        {/* Column 3: Actions */}
        <div className={styles.tableCell}>
          <div className={styles.actions}>
            {canManageThisOrg && (
              <>
                <button
                  className={styles.actionLink}
                  onClick={() =>
                    onCreateSubgroup(organization, organization.id)
                  }
                >
                  <IconCirclePlus />
                  <span>Ny undergruppe</span>
                </button>
                <button
                  className={styles.actionLink}
                  onClick={() => onManageMembers(organization, organization.id)}
                >
                  <IconMembers />
                  <span>Administrér medlemmer</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Column 4: Hide/Show and Enable/Disable */}
        <div className={styles.tableCell}>
          <div className={styles.actions}>
            {canManageThisOrg && (
              <button
                className={styles.actionLink}
                onClick={() =>
                  onToggleHidden(organization, false, organization.id)
                }
              >
                {organization.hidden ? <IconEyeOff /> : <IconEye />}
                <span>{organization.hidden ? "Vis gruppe" : "Skjul gruppe"}</span>
              </button>
            )}
            {isSystemAdmin && !hasFeideMapping && organization.type === 'organization' && (organization.active || showInactive) && (
              <button
                className={styles.actionLink}
                onClick={() => onToggleActive(organization.id, organization.active)}
                title={organization.active ? "Deaktiver organisasjon" : "Aktiver organisasjon"}
              >
                <span>{organization.active ? "Deaktiver" : "Aktiver"}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Render children recursively */}
      {isExpanded &&
        hasChildren &&
        integratedTree.map((group) => (
          <GroupRow
            key={`${group.isFeide ? "feide" : "digup"}-${group.id}`}
            group={group}
            organizationId={organization.id}
            level={level + 1}
            expandedNodes={expandedNodes}
            onToggleNode={onToggleNode}
            canManage={canManage}
            canManageGroup={canManageGroup}
            tree={tree}
            groupMemberships={groupMemberships}
            isSystemAdmin={isSystemAdmin}
            showInactive={showInactive}
            onCreateSubgroup={onCreateSubgroup}
            onManageMembers={onManageMembers}
            onImportOrMap={onImportOrMap}
            onToggleHidden={onToggleHidden}
            onToggleActive={onToggleActive}
          />
        ))}
    </>
  );
};

// Recursive group row component
const GroupRow = ({
  group,
  organizationId,
  level,
  expandedNodes,
  onToggleNode,
  canManage,
  canManageGroup,
  tree,
  groupMemberships,
  isSystemAdmin,
  showInactive,
  onCreateSubgroup,
  onManageMembers,
  onImportOrMap,
  onToggleHidden,
  onToggleActive,
}) => {
  const nodeKey = `${group.isFeide ? "feide" : "digup"}-${group.id}`;
  const isExpanded = expandedNodes.has(nodeKey);
  const hasChildren = group.children && group.children.length > 0;
  const hasFeideMapping = !group.isFeide && group.feideGroup;

  // Check if user can manage THIS specific group
  const canManageThisGroup = !group.isFeide && canManageGroup ? canManageGroup(group.id, tree) : false;

  const paddingLeft = level * 2; // 2rem per level

  return (
    <>
      {/* Group Row */}
      <div className={styles.tableRow}>
        {/* Column 1: Name with expand button */}
        <div
          className={styles.tableCell}
          style={{ paddingLeft: `${paddingLeft}rem` }}
        >
          <div className={styles.nameCell}>
            {hasChildren && (
              <button
                className={styles.expandButton}
                onClick={() => onToggleNode(nodeKey)}
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? (
                  <IconCircleChevronUp />
                ) : (
                  <IconCircleChevronDown />
                )}
              </button>
            )}
            <span className={`${styles.groupName} ${!group.isFeide && !group.active ? styles.inactiveOrg : ''}`}>
              {group.name}
              <span className={styles.groupId}> (ID: {group.id})</span>
            </span>
          </div>
        </div>

        {/* Column 2: Source */}
        <div className={styles.tableCell}>
          {group.isFeide ? (
            <span className={styles.sourceText}>Feide</span>
          ) : hasFeideMapping ? (
            <div className={styles.syncedSource}>
              <span className={styles.sourceText}>Feide</span>
              <IconSync className={styles.syncIcon} />
              <span className={styles.sourceText}>DigUp</span>
            </div>
          ) : (
            <span className={styles.sourceText}>DigUp</span>
          )}
        </div>

        {/* Column 3: Actions */}
        <div className={styles.tableCell}>
          <div className={styles.actions}>
            {canManageThisGroup && (
              <>
                {group.isFeide ? (
                  <button
                    className={styles.actionLink}
                    onClick={() => onImportOrMap(group, organizationId)}
                  >
                    <IconFileImport />
                    <span>Importér eller kopla til gruppe</span>
                  </button>
                ) : (
                  <>
                    <button
                      className={styles.actionLink}
                      onClick={() => onCreateSubgroup(group, organizationId)}
                    >
                      <IconCirclePlus />
                      <span>Ny undergruppe</span>
                    </button>
                    <button
                      className={styles.actionLink}
                      onClick={() => onManageMembers(group, organizationId)}
                    >
                      <IconMembers />
                      <span>Administrér medlemmer</span>
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Column 4: Hide/Show and Enable/Disable */}
        <div className={styles.tableCell}>
          <div className={styles.actions}>
            {canManageThisGroup && (
              <button
                className={styles.actionLink}
                onClick={() =>
                  onToggleHidden(group, group.isFeide, organizationId)
                }
              >
                {group.hidden ? <IconEyeOff /> : <IconEye />}
                <span>{group.hidden ? "Vis gruppe" : "Skjul gruppe"}</span>
              </button>
            )}
            {isSystemAdmin && !hasFeideMapping && group.type === 'organization' && (group.active || showInactive) && (
              <button
                className={styles.actionLink}
                onClick={() => onToggleActive(group.id, group.active)}
                title={group.active ? "Deaktiver organisasjon" : "Aktiver organisasjon"}
              >
                <span>{group.active ? "Deaktiver" : "Aktiver"}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Render children recursively */}
      {isExpanded &&
        hasChildren &&
        group.children.map((child) => (
          <GroupRow
            key={`${child.isFeide ? "feide" : "digup"}-${child.id}`}
            group={child}
            organizationId={organizationId}
            level={level + 1}
            expandedNodes={expandedNodes}
            onToggleNode={onToggleNode}
            canManage={canManage}
            canManageGroup={canManageGroup}
            tree={tree}
            groupMemberships={groupMemberships}
            isSystemAdmin={isSystemAdmin}
            showInactive={showInactive}
            onCreateSubgroup={onCreateSubgroup}
            onManageMembers={onManageMembers}
            onImportOrMap={onImportOrMap}
            onToggleHidden={onToggleHidden}
            onToggleActive={onToggleActive}
          />
        ))}
    </>
  );
};

export default OrganizationSettings;
